import React from "react";
import {action, observable, computed, makeObservable} from "mobx";

export class UiState {
    activeTool: string;
    language: string;
    menuVisible: boolean;
    pendingRequestCount: number;
    sidebarSelectOpen: boolean;
    sidebarVisible: boolean;
    sidebarHigh: boolean;
    filterActive: boolean;
    widgets = observable.map<string, React.Component>();

    /**
     * App window size
     */
    uiWidth: number | undefined;
    uiHeight: number | undefined;
    uiOffsetTop: number | undefined;
    uiOrientation: "landscape" | "portrait" | undefined;
    isSmallDevice: boolean | undefined;

    /**
     * Remember selected tab per station
     */
    selectedTabs = observable.map<number, number>();

    /**
     * Resizing state of a popup window
     */
    resizing = observable.box(false);
    toolGraphic: __esri.Graphic | null = null;

    sublayerMap = new Map<string, string>();

    constructor() {
        makeObservable(this, {
            activeTool: observable,
            language: observable,
            menuVisible: observable,
            pendingRequestCount: observable,
            sidebarSelectOpen: observable,
            sidebarVisible: observable,
            sidebarHigh: observable,
            filterActive: observable,
            widgets: observable,
            uiWidth: observable,
            uiHeight: observable,
            uiOffsetTop: observable,
            uiOrientation: observable,
            isSmallDevice: observable,
            selectedTabs: observable,
            resizing: observable,
            toolGraphic: observable,

            appIsInSync: computed,
            isSidebarVisible: computed,
            isResizing: computed,

            addWidget: action,
            removeWidget: action,
            setActiveTool: action,
            setLanguage: action,
            setUiSize: action,
            setUiOffsetTop: action,
            resetPendingRequestCount: action,
            setMenuVisible: action,
            setFilterActive: action,
            setSidebarSelectOpen: action,
            setSidebarVisible: action,
            setToolGraphic: action,
            toggleSidebarVisible: action,
            setSidebarHigh: action,
            startResizing: action,
            stopResizing: action
        });
        this.setActiveTool("");
        this.setLanguage("de-CH");
        this.resetPendingRequestCount();
        this.setSidebarVisible(false);
        this.setUiOffsetTop();
        this.handleWindowResizeWhenSidebarOpen = this.handleWindowResizeWhenSidebarOpen.bind(this);
    }

    get appIsInSync() {
        return this.pendingRequestCount === 0;
    }
    get isResizing(): boolean {
        return this.resizing.get();
    }
    get isSidebarVisible() {
        return this.sidebarVisible;
    }

    addWidget(key: string, widget: React.Component) {
        this.widgets.set(key, widget);
    }
    removeWidget(key: string) {
        this.widgets.delete(key);
    }

    setActiveTool(tool: string, graphic?: __esri.Graphic | null) {
        this.setToolGraphic(graphic);
        this.activeTool = tool;
    }

    setLanguage(lang: string) {
        this.language = lang;
    }

    setUiSize(width: number | undefined, height: number | undefined, isSmallDevice?: boolean) {
        if(typeof width === "number" && typeof height === "number") {
            this.uiWidth = width;
            this.uiHeight = height;
            this.uiOrientation = height > width? "portrait": "landscape";
            this.isSmallDevice = isSmallDevice;
        }
    }
    setUiOffsetTop(uiOffsetTop?: number) {
        this.uiOffsetTop = uiOffsetTop? uiOffsetTop: 0;
    }

    resetPendingRequestCount() {
        this.pendingRequestCount = 0;
    }

    setMenuVisible(visible: boolean) {
        this.menuVisible = visible;
    }
    setFilterActive(active: boolean) {
        this.filterActive = active;
    }

    setSidebarSelectOpen(open: boolean) {
        this.sidebarSelectOpen = open;
    }

    setSidebarVisible(visible: boolean) {
        if (visible) {
            // add listener when menu open
            window.addEventListener("resize", this.handleWindowResizeWhenSidebarOpen);
        } else {
            window.removeEventListener("resize", this.handleWindowResizeWhenSidebarOpen);
        }
        this.sidebarVisible = visible;
    }
    toggleSidebarVisible() {
        this.setSidebarVisible(!this.sidebarVisible);
    }
    setSidebarHigh(isHigh: boolean){
        this.sidebarHigh = isHigh;
    }

    setToolGraphic(toolGraphic?: __esri.Graphic | null) {
        if(typeof toolGraphic === "undefined"){
            toolGraphic = null;
        }
        this.toolGraphic = toolGraphic;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleWindowResizeWhenSidebarOpen() {
        if (this.isSidebarVisible) {
            this.setSidebarVisible(false);
        }
        // only resize once
        window.removeEventListener("resize", this.handleWindowResizeWhenSidebarOpen);
    }

    /**
     * Start resizing
     */
    startResizing() {
        this.resizing.set(true);
    }

    /**
     * Stop resizing
     */
    stopResizing() {
        this.resizing.set(false);
    }

}
